/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { LocationPermissions, Wasting, PharmacyPermissions } from '@midas/shared/common';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { UserSelectors } from './stores/user';
import { UserPermissionsActions, UserPermissionsSelectors } from './stores/user-location-permissions';

@Injectable({ providedIn: 'root' })
export class LocationPermissionsService {
  constructor(private store: Store) {}

  public checkTenantLocationPermissions(
    locationId: number | null,
    neededPermissions: LocationPermissions,
  ): Observable<boolean> {
    return this.store.select(UserPermissionsSelectors.getUserPermissions).pipe(
      filter(({ loaded }) => {
        if (!loaded) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(({ userLocationPermissions: userPermissions }): boolean => {
        const perm = userPermissions.find((l) => l.locationId === locationId);
        if (!perm || !perm.permissions) {
          return false;
        }

        if ((perm.permissions & neededPermissions) === neededPermissions) {
          return true;
        }

        return false;
      }),
    );
  }

  public tenantHasPermissionsAtAnyLocation(neededPermissions: LocationPermissions): Observable<boolean> {
    return this.store.select(UserPermissionsSelectors.getUserPermissions).pipe(
      filter(({ loaded, loading }) => {
        if (!loaded && !loading) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(({ userLocationPermissions: userPermissions }): boolean => {
        return userPermissions.some((p) => p.permissions & neededPermissions);
      }),
    );
  }

  public tenantHasMultiplePermissionsAtAnyLocation(
    neededPermissions: LocationPermissions[],
  ): Observable<boolean> {
    return this.store.select(UserPermissionsSelectors.getUserPermissions).pipe(
      filter(({ loaded, loading }) => {
        if (!loaded && !loading) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(({ userLocationPermissions: userPermissions }): boolean => {
        return neededPermissions.every((np) => userPermissions.some((up) => up.permissions & np));
      }),
    );
  }

  public canWitnessWaste(waste: Wasting): Observable<boolean> {
    return combineLatest([
      this.store.select(UserPermissionsSelectors.getUserPermissions),
      this.store.select(UserSelectors.getUserState),
    ]).pipe(
      filter(([{ loaded, loading }]) => {
        if (!loaded && !loading) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(([{ userLocationPermissions: userPermissions }, { id }]): boolean => {
        const wasteUserIsMe = waste.wasteUserId === id;
        if (wasteUserIsMe) {
          return false;
        }

        const witnessLocationIds = userPermissions
          .filter((p) => p.permissions & LocationPermissions.CanWitness)
          .map((p) => p.locationId);

        return witnessLocationIds.includes(waste.locationId);
      }),
    );
  }

  public canResolveWaste(waste: Wasting): Observable<boolean> {
    return combineLatest([
      this.store.select(UserPermissionsSelectors.getUserPermissions),
      this.store.select(UserSelectors.getUserState),
    ]).pipe(
      filter(([{ loaded, loading }]) => {
        if (!loaded && !loading) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(([{ userLocationPermissions: userPermissions }, { id }]): boolean => {
        const wasteUserIsMe = waste.wasteUserId === id;
        if (wasteUserIsMe) {
          return false;
        }

        const resolveLocationIds = userPermissions
          .filter((p) => p.permissions & LocationPermissions.CanResolve)
          .map((p) => p.locationId);

        return resolveLocationIds.includes(waste.locationId);
      }),
    );
  }

  public canViewHistoricalWaste(waste: Wasting): Observable<boolean> {
    return this.store.select(UserPermissionsSelectors.getUserPermissions).pipe(
      filter(({ loaded, loading }) => {
        if (!loaded && !loading) {
          this.store.dispatch(UserPermissionsActions.getUserLocationPermissions());
        }
        return loaded;
      }),
      map(({ userLocationPermissions: userPermissions }): boolean => {
        const historicalLocationIds = userPermissions
          .filter((p) => p.permissions & LocationPermissions.CanViewHistoricalWastes)
          .map((p) => p.locationId);

        return historicalLocationIds.includes(waste.locationId);
      }),
    );
  }

  public hasPharmacyPermissions(): Observable<boolean> {
    return this.tenantHasMultiplePermissionsAtAnyLocation(PharmacyPermissions);
  }
}
